// Relive-style photo moments for the flyover VIDEO renderer (lib/flyover_renderer/entry.ts).
//
// The pure half: where each stop halts the flight, and the choreography of a stop — the first photo
// grows out of its pin on the route to fill the frame, the stop's photos play as a swipe slideshow
// (each with a slow push-in, swiped off to the left as the next comes in from the right), and the last
// shrinks back into the pin before the flight carries on. Flyover::PhotoMoments chooses the stops (photos taken within 150 m of each other
// are one stop); this file only places and animates them.
//
// Everything is a function of numbers the renderer already has (coords, clip time, image size), so it
// is Jest-tested without a browser or a map.

import { projectOntoEdge } from "./flyoverPeaks";

/** One photo in a stop. */
export interface FlyoverPhoto {
  id: string;
  url: string;
}

/** One stop, as Flyover::PhotoMoments writes it into render_config.photoMoments: where, and its photos. */
export interface FlyoverPhotoStopConfig {
  id: string;
  lng: number;
  lat: number;
  photos: FlyoverPhoto[];
}

/** A stop pinned to the flyover's own timeline: `frac` is 0..1 of the distance flown, `at` the spot on the line. */
export interface PlacedStop {
  stop: FlyoverPhotoStopConfig;
  frac: number;
  at: [number, number];
}

/** Same reach as the server's Flyover::PhotoMoments::MAX_OFF_ROUTE_M. */
export const MAX_OFF_ROUTE_M = 400;
const SAME_PASS_TOLERANCE_M = 15;
/**
 * Stops are kept off the very ends: the body starts AT 0, so a stop there would never be crossed, and
 * one at 1 would collide with the outro. A trailhead photo still shows, a moment into the flight.
 */
export const STOP_EDGE = 0.02;
/**
 * Two stops closer than this would land on the same frame; the second's photos join the first's
 * slideshow rather than being lost (every photo sent is shown).
 */
const MIN_STOP_GAP = 0.0005;

/**
 * Where along the flown line each stop halts the flight, in walking order. Projected onto the coords
 * the renderer actually flies (a decimated copy of the track), exactly as placePeaks does for summits.
 */
export function placeStops(stops: FlyoverPhotoStopConfig[] | null | undefined, coords: number[][], cumKm: number[]): PlacedStop[] {
  if (!Array.isArray(stops) || stops.length === 0 || coords.length < 2) return [];
  const totalKm = cumKm[cumKm.length - 1] || 0;
  if (totalKm <= 0) return [];
  const placed: PlacedStop[] = [];
  for (const raw of stops) {
    const photos = Array.isArray(raw?.photos) ? raw.photos.filter((p) => p && p.url) : [];
    if (!photos.length || !Number.isFinite(raw.lng) || !Number.isFinite(raw.lat)) continue;
    const p = [raw.lng, raw.lat];
    const passes: { off: number; km: number; at: [number, number] }[] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      const [t, off] = projectOntoEdge(p, coords[i], coords[i + 1]);
      const a = coords[i], b = coords[i + 1];
      passes.push({ off, km: cumKm[i] + t * (cumKm[i + 1] - cumKm[i]), at: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t] });
    }
    const best = Math.min(...passes.map((c) => c.off));
    if (best > MAX_OFF_ROUTE_M) continue;
    const first = passes.filter((c) => c.off <= best + SAME_PASS_TOLERANCE_M).reduce((x, y) => (y.km < x.km ? y : x));
    const frac = Math.min(Math.max(first.km / totalKm, STOP_EDGE), 1 - STOP_EDGE);
    placed.push({ stop: { ...raw, photos }, frac, at: first.at });
  }
  placed.sort((a, b) => a.frac - b.frac);
  const merged: PlacedStop[] = [];
  for (const p of placed) {
    const prev = merged[merged.length - 1];
    if (prev && p.frac - prev.frac < MIN_STOP_GAP) prev.stop = { ...prev.stop, photos: [...prev.stop.photos, ...p.stop.photos] };
    else merged.push(p);
  }
  return merged;
}

// ---------------------------------------------------------------- the stop

/** The first photo grows out of its pin over this long… */
export const PHOTO_GROW_MS = 450;
/** …each photo holds the frame for a second — one photo per second (a lone stop: 1.9s in all)… */
export const PHOTO_SLIDE_MS = 1000;
/** …the last of each slide swipes over to the next photo… */
export const PHOTO_SWIPE_MS = 400;
/** …and the last photo shrinks back into the pin over this long. */
export const PHOTO_SHRINK_MS = 450;
/** The slow push-in on each photo across its slide (Ken Burns). */
export const PHOTO_PUSH_IN = 0.06;

/** How long a stop with `count` photos holds the flight, grow to shrink. */
export function stopMs(count: number): number {
  return count > 0 ? PHOTO_GROW_MS + count * PHOTO_SLIDE_MS + PHOTO_SHRINK_MS : 0;
}

/** Total time the stops add to the clip (one entry per stop: its photo count). The 45s fit budgets it. */
export function photoHoldMs(counts: number[]): number {
  return counts.reduce((sum, n) => sum + stopMs(n), 0);
}

const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);
const easeInCubic = (x: number) => x * x * x;
const easeInOutCubic = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const clamp01 = (x: number) => Math.min(Math.max(x, 0), 1);

/**
 * How open the stop is at `elapsedMs` into it: 0 = sitting in its pin, 1 = filling the frame. Eases out
 * of the pin, holds through the whole slideshow, and eases back in.
 */
export function photoOpen(elapsedMs: number, count: number): number {
  const total = stopMs(count);
  if (elapsedMs <= 0 || elapsedMs >= total) return 0;
  if (elapsedMs < PHOTO_GROW_MS) return easeOutCubic(elapsedMs / PHOTO_GROW_MS);
  const shrinkFrom = total - PHOTO_SHRINK_MS;
  if (elapsedMs > shrinkFrom) return 1 - easeInCubic((elapsedMs - shrinkFrom) / PHOTO_SHRINK_MS);
  return 1;
}

/** Which photo of the slideshow is up, and how far it has swiped over to the next (0..1). */
export interface Slide {
  index: number;
  /** The photo being swiped to, or -1 outside a swipe. */
  next: number;
  mix: number;
  /** Time into this photo's own slide, for its push-in. */
  slideMs: number;
}

export function slideAt(elapsedMs: number, count: number): Slide {
  if (count <= 0) return { index: 0, next: -1, mix: 0, slideMs: 0 };
  // The grow belongs to the first photo and the shrink to the last, so the push-in never jumps.
  const t = Math.max(0, elapsedMs - PHOTO_GROW_MS);
  const index = Math.min(Math.floor(t / PHOTO_SLIDE_MS), count - 1);
  const slideMs = t - index * PHOTO_SLIDE_MS + (index === 0 ? Math.min(elapsedMs, PHOTO_GROW_MS) : 0);
  const swipeFrom = PHOTO_SLIDE_MS - PHOTO_SWIPE_MS;
  const into = t - index * PHOTO_SLIDE_MS;
  if (index < count - 1 && into > swipeFrom) {
    return { index, next: index + 1, mix: easeInOutCubic(clamp01((into - swipeFrom) / PHOTO_SWIPE_MS)), slideMs };
  }
  return { index, next: -1, mix: 0, slideMs };
}

/** How far the dip in size goes at the middle of a swipe, for a little depth. */
export const SWIPE_DIP = 0.08;

/**
 * Where the two cards are mid-swipe: the outgoing one slid `outDx` (left), the incoming one `inDx`
 * (still to the right, arriving), both dipped to `scale`. `distance` is how far a card travels — the
 * width it is laid out in plus a gap, so neither card is ever cut off by the other.
 */
export function swipeLayout(mix: number, distance: number): { outDx: number; inDx: number; scale: number } {
  const m = clamp01(mix);
  return { outDx: -m * distance, inDx: (1 - m) * distance, scale: 1 - SWIPE_DIP * Math.sin(Math.PI * m) };
}

/** The page dot lit for a slide: the incoming photo's once it is more than halfway in. */
export function activeDot(slide: Slide): number {
  return slide.next >= 0 && slide.mix > 0.5 ? slide.next : slide.index;
}

/** Scale of a photo inside its frame: a steady push-in from 1 across its slide. */
export function photoZoom(slideMs: number): number {
  return 1 + PHOTO_PUSH_IN * clamp01(slideMs / (PHOTO_GROW_MS + PHOTO_SLIDE_MS + PHOTO_SHRINK_MS));
}

// ---------------------------------------------------------------- geometry

export interface Rect { x: number; y: number; w: number; h: number }
export interface Bounds { left: number; top: number; right: number; bottom: number }

/** The largest rect of the image's aspect that fits in bounds, centred in it. */
export function fitPhotoRect(imgW: number, imgH: number, bounds: Bounds): Rect {
  const bw = Math.max(bounds.right - bounds.left, 1), bh = Math.max(bounds.bottom - bounds.top, 1);
  const aspect = imgW > 0 && imgH > 0 ? imgW / imgH : 4 / 3;
  let w = bw, h = bw / aspect;
  if (h > bh) { h = bh; w = bh * aspect; }
  return { x: bounds.left + (bw - w) / 2, y: bounds.top + (bh - h) / 2, w, h };
}

/** The frame part-way between two rects: the pin and a photo, or one photo and the next. */
export function lerpRect(from: Rect, to: Rect, t: number): Rect {
  const k = clamp01(t);
  return {
    x: from.x + (to.x - from.x) * k,
    y: from.y + (to.y - from.y) * k,
    w: from.w + (to.w - from.w) * k,
    h: from.h + (to.h - from.h) * k
  };
}

// ---------------------------------------------------------------- the clock

/**
 * Which stop is up, and for how long. The flyover controller reports each stop it lands on; the
 * renderer then holds the flight and ticks this clock once per frame until the stop is over.
 */
export class PhotoStops {
  private activeIndex = -1;
  private elapsed = 0;
  private duration = 0;
  private readonly shown = new Set<number>();

  /** The flight just landed on stop `index`, which holds for `durationMs` (stopMs of its photos). */
  begin(index: number, durationMs: number): void {
    this.activeIndex = index;
    this.elapsed = 0;
    this.duration = durationMs;
    this.shown.add(index);
  }

  /** Advance the stop by one frame. Returns true while the flight should still be held. */
  tick(dtMs: number): boolean {
    if (this.activeIndex < 0) return false;
    this.elapsed += dtMs;
    if (this.elapsed >= this.duration) { this.activeIndex = -1; this.elapsed = 0; return false; }
    return true;
  }

  holding(): boolean { return this.activeIndex >= 0; }

  active(): { index: number; elapsedMs: number } | null {
    return this.activeIndex >= 0 ? { index: this.activeIndex, elapsedMs: this.elapsed } : null;
  }

  /** Whether the flight has already stopped at stop `index` (its pin then stays on the route). */
  wasShown(index: number): boolean { return this.shown.has(index); }

  reset(): void { this.activeIndex = -1; this.elapsed = 0; this.duration = 0; this.shown.clear(); }
}
